import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAdminContext } from '../AdminContext';

export default function SchedulerTab() {
  const {
    isLoadingScheduler,
    schedulerSettings,
    handleUpdateSchedulerSettings,
    isUpdatingScheduler,
    handleRunNightlyJobs,
    isRunningNightlyJobs,
    loadNightlyJobHistory,
    isLoadingHistory,
    nightlyJobHistory,
  } = useAdminContext();

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Scheduled Tasks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control when automated tasks run for this subreddit. Toggle tasks on or off as needed.
      </Typography>

      {isLoadingScheduler ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : schedulerSettings ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>Message Listener</Typography>
                <Typography variant="body2" color="text.secondary">Checks Reddit inbox for messages requesting flair changes. Runs every minute.</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Message format: Send a message to the bot with subject "flair-[subreddit]" and body containing flair name(s). For double flair, separate with " / " (space, slash, space). The bullet separator " • " also works.
                </Typography>
              </Box>
              <Switch
                checked={schedulerSettings.enable_message_listener}
                onChange={(e) => handleUpdateSchedulerSettings('enable_message_listener', e.target.checked)}
                disabled={isUpdatingScheduler}
              />
            </Box>
            {schedulerSettings.next_message_check && (
              <Typography variant="caption" color="text.secondary">Next check: {new Date(schedulerSettings.next_message_check).toLocaleString()}</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>Nightly Jobs</Typography>
                <Typography variant="body2" color="text.secondary">Runs at 2 AM ET (7 AM UTC) daily. Includes:</Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  <li><Typography variant="body2" color="text.secondary">Push changes to subscribed subreddits</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Pull user flair from Reddit</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Validate flairs, enqueue Reddit fixes, drain in 90-user batches</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Update flair history</Typography></li>
                </Box>
              </Box>
              <Switch
                checked={schedulerSettings.enable_nightly_jobs}
                onChange={(e) => handleUpdateSchedulerSettings('enable_nightly_jobs', e.target.checked)}
                disabled={isUpdatingScheduler}
              />
            </Box>
            {schedulerSettings.next_nightly_job && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Next run: {new Date(schedulerSettings.next_nightly_job).toLocaleString()}</Typography>
            )}
            <Button variant="contained" color="primary" onClick={handleRunNightlyJobs} disabled={isRunningNightlyJobs} sx={{ mt: 1 }}>
              {isRunningNightlyJobs ? 'Running...' : 'Run Now'}
            </Button>
          </Paper>

          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Job History</Typography>
              <Button size="small" variant="outlined" onClick={loadNightlyJobHistory} disabled={isLoadingHistory}>Refresh</Button>
            </Box>
            {isLoadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : nightlyJobHistory.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Started</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Users Updated</TableCell>
                      <TableCell>History Entries</TableCell>
                      <TableCell>Triggered By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nightlyJobHistory.map((job: any) => (
                      <TableRow key={job.id}>
                        <TableCell>{new Date(job.started_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip label={job.status} color={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : job.status === 'running' ? 'info' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>{job.duration_seconds ? `${job.duration_seconds}s` : '-'}</TableCell>
                        <TableCell>{job.users_updated || 0}</TableCell>
                        <TableCell>{job.history_entries || 0}</TableCell>
                        <TableCell>{job.triggered_by || 'Scheduled'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No job history yet. Jobs will appear here after they run.</Typography>
            )}
          </Paper>

          {isUpdatingScheduler && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          )}
        </Box>
      ) : (
        <Alert severity="warning">No scheduler settings found. Please ensure Reddit authorization is set up first.</Alert>
      )}
    </Paper>
  );
}
